import { Component, ElementRef, Input, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { NavParams, ViewController, TextInput } from 'ionic-angular';
import { keyboardCapsLockLayout, KeyboardLayout, specialKeys, getLayout } from './layouts';
import { IonVirtualKeyboardService } from './ion-virtual-keyboard.service';
import { KeyPressInterface } from './key-press.interface';



/**
 * Virtual keyboard component.
 *
 * @export
 * @class IonVirtualKeyboardComponent
 * @implements {AfterViewInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'ion-virtual-keyboard',
  template: `
    <ion-card text-center [style.min-width.px]="minWidth">
      <ion-card-header text-left *ngIf="title">{{title}}</ion-card-header>
      <ion-item no-margin>
        <button no-margin style="width:132px; margin:0px" item-end default ion-button  color="primary"
          (click)="close()"
        >
          <ion-icon name="checkmark-circle"></ion-icon>
        </button>

        <ion-input type="text"
          #keyboardInput
          (click)="updateCaretPosition()"
          (keypress)="keyPressHandler($event)"
          [(ngModel)]="value" placeholder="{{ placeholder }}"
          [maxlength]="maxLength"
        ></ion-input>
      </ion-item>
      
        <ion-buttons *ngFor="let row of layout; let rowIndex = index"
          [attr.data-index]="rowIndex">
              <ion-virtual-keyboard-key *ngFor="let key of row; let keyIndex = index"
              [key]="key"
              [disabled]="disabled"
              [attr.data-index]="keyIndex"
              (keyPress)="keyPress($event)"
            ></ion-virtual-keyboard-key>
        </ion-buttons>
    </ion-card>
  `,
  styles: [`
    .close {
      position: relative;
      float: right;
      top: -16px;
      right: 0;
      margin-bottom: -40px;
    }
  
    .mat-input-container {
      margin: -16px 0;
      font-size: 32px;
    }
  
    .mat-input-element:disabled {
      color: currentColor;
    }

    :host /deep/ .mat-input-placeholder {
      top: 10px !important;
      font-size: 24px !important;
    }
  `]
})
export class IonVirtualKeyboardComponent implements AfterViewInit, OnDestroy {

  /**
   * A reference to the input as ElementRef.
   *
   * @type {ElementRef}
   * @memberof IonVirtualKeyboardComponent
   */
  @ViewChild('keyboardInput', { read: ElementRef }) keyboardInput: ElementRef;

  /**
   * A reference to the input as TextInput.
   *
   * @type {TextInput}
   * @memberof IonVirtualKeyboardComponent
   */
  @ViewChild('keyboardInput') ionKeyboardInput: TextInput;


  /**
   * Set width calc automatically.
   *
   * @type {boolean}
   * @memberof IonVirtualKeyboardComponent
   */
  @Input('auto-fit') autoFit: boolean = true;


  /**
   * A title to show.
   *
   * @type {string}
   * @memberof IonVirtualKeyboardComponent
   */
  @Input('title') title: string = '';


  /**
   * A input mask to make padding.
   *
   * @type {string}
   * @memberof IonVirtualKeyboardComponent
   */
  @Input('mask') mask: string = '';


  /**
   * The layout to display.
   *
   * @private
   * @type {KeyboardLayout}
   * @memberof IonVirtualKeyboardComponent
   */
  private _layout: KeyboardLayout = [];


  /**
   * Internal value.
   *
   * @private
   * @type {string}
   * @memberof IonVirtualKeyboardComponent
   */
  private _value: string = '';


  private _inputElement: ElementRef = null;


  /**
   * The placeholder.
   *
   * @type {string}
   * @memberof IonVirtualKeyboardComponent
   */
  public placeholder: string;

  /**
   * Disables the input.
   *
   * @type {boolean}
   * @memberof IonVirtualKeyboardComponent
   */
  public disabled: boolean;


  /**
   * Max length allowed.
   *
   * @type {(number|string)}
   * @memberof IonVirtualKeyboardComponent
   */
  public maxLength: number|string;


  /**
   * Cursor position.
   *
   * @private
   * @type {number}
   * @memberof IonVirtualKeyboardComponent
   */
  private caretPosition: number;

  /**
   * Shit pressed.
   *
   * @private
   * @memberof IonVirtualKeyboardComponent
   */
  private shift = false;


  /**
   * Min width of the component
   *
   * @private
   * @memberof IonVirtualKeyboardComponent
   */
  private minWidth = null;


  /**
   * Value getter.
   *
   * @readonly
   * @type {string}
   * @memberof IonVirtualKeyboardComponent
   */
  get value(): string {
    return this._value;
  }

  /**
   * Value setter.
   *
   * @memberof IonVirtualKeyboardComponent
   */
  @Input('value')
  set value(value: string) {
    this._value = value;
  }

  /**
   * Layout getter.
   *
   * @readonly
   * @type {KeyboardLayout}
   * @memberof IonVirtualKeyboardComponent
   */
  get layout(): KeyboardLayout{
    return this._layout;
  }


  /**
   * Layout setter.
   *
   * @memberof IonVirtualKeyboardComponent
   */
  @Input('layout') 
  set layout(value: KeyboardLayout) {
    if(typeof value == 'string' ) value = getLayout(value);
    this._layout = value;

}
  

  /**
   * Input element  getter.
   *
   * @readonly
   * @type {ElementRef}
   * @memberof IonVirtualKeyboardComponent
   */
  get inputElement(): ElementRef {
    if(!this._inputElement){
      
      let mock:any = {'nativeElement':{'value': null}};
      return mock;
    }
    return this._inputElement;
  }

  
  /**
   * Input element setter.
   *
   * @memberof IonVirtualKeyboardComponent
   */
  @Input('inputElement')
  set inputElement(value: ElementRef) {
      this._inputElement = value;
  }

  /**
   * Helper method to set cursor in input to correct place.
   *
   * @param {HTMLInputElement|HTMLTextAreaElement}  input
   * @param {number}                                start
   * @param {number}                                end
   */
  private static setSelectionRange(
    input: any,
    start: number,
    end: number
  ): void {
    if (input.setSelectionRange) {
      input.focus();
      input.setSelectionRange(start, end);

    } else if (input.createTextRange) {
      const range = input.createTextRange();

      range.collapse(true);
      range.moveEnd('character', end);
      range.moveStart('character', start);
      range.select();
    }
  }


  /**
   * Creates an instance of IonVirtualKeyboardComponent.
   * 
   * @param {IonVirtualKeyboardService} virtualKeyboardService //VK service.
   * @param {ViewController} _viewController //Ionic view controller.
   * @param {NavParams} _navParams //Ionic param controllers.
   * @memberof IonVirtualKeyboardComponent
   */
  public constructor(
    private virtualKeyboardService: IonVirtualKeyboardService,
    private _viewController: ViewController,
    private _navParams: NavParams
  ) { 

   
  }

  /**
   * AfterViewInit life cycle hook, this will do following:
   *  1) Get the nav params if exist.
   *  2) Calc width if auto-fit is enabled.
   *  3) Apply mask if passed.
   *
   * @memberof IonVirtualKeyboardComponent
   */
  public ngOnInit(){
    this.setNavParams();
    if(this.autoFit) this.calcCardWidth();
    this.applyMask();
  }

  /**
   *  AfterViewInit life cycle hook, this will do following:
   *  1) Set focus to virtual keyboard input field
   *  2) Subscribe to following
   *    2.1) Shift key, this is needed in keyboard event dispatches
   *    2.2) CapsLock key, this will change keyboard layout
   *    2.3) Caret position in virtual keyboard input
   *  3) Reset of possible previously tracked caret position
   *
   * @memberof IonVirtualKeyboardComponent
   */
  public ngAfterViewInit(): void {

   

    this.virtualKeyboardService.shift$.subscribe((shift: boolean) => {
      this.shift = shift;
    });

    this.virtualKeyboardService.capsLock$.subscribe((capsLock: boolean) => {
      this.layout = keyboardCapsLockLayout(this.layout, capsLock);
    });

    this.virtualKeyboardService.caretPosition$.subscribe((caretPosition: number) => {
      this.caretPosition = caretPosition;

      setTimeout(() => {
        IonVirtualKeyboardComponent.setSelectionRange(this.keyboardInput.nativeElement, caretPosition, caretPosition);
      }, 0);
    });


    if(this.value === null) this.value = '';
    if (this.value.length) {
      this.virtualKeyboardService.setCaretPosition(this.value.length);
    }

    //this.maxLength = this.inputElement.nativeElement.maxLength > 0 ? this.inputElement.nativeElement.maxLength : '';

    this.checkDisabled();

    setTimeout(() => {
      this.ionKeyboardInput.setFocus();
    }, 700);
  }


  /**
   * Extract the nav params if exist
   *
   * @memberof IonVirtualKeyboardComponent
   */
  setNavParams(){
    let input = this._navParams.get('input');
    if(input) this.inputElement = input;
    let layout = this._navParams.get('layout');
    if(layout) this.layout = this._navParams.get('layout');
    let value = this._navParams.get('value');
    if(value) this.value = value;
    let title = this._navParams.get('title');
    if(title) this.title = title;
    let autoFit = this._navParams.get('auto-fit');
    if(autoFit === false) this.autoFit = autoFit;
    let mask = this._navParams.get('mask');
    if(mask) this.mask = mask;
    
  }

  /**
   * On destroy life cycle hook, in this we want to reset virtual keyboard service states on following:
   *  - Shift
   *  - CapsLock
   */
  public ngOnDestroy(): void {
    this.virtualKeyboardService.reset();
  }

  /**
   * Method to close virtual keyboard dialog and send value
   */
  public close(): void {
   
    let value = this.value;
    if(this.mask && this.value.length!=this.mask.length){
      if(this.value.length<3) return;
      let prefix = this.mask.substr(0,2);
      let val = value.substr(2, value.length - 1);
      let dif = this.mask.length - (prefix.length + val.length);
      value = prefix;
      for(let i=0; i<dif; i++){
        value += '0';
      }
      value+= val;
    } 

    this._viewController.dismiss({value: value});
  }

  /**
   * Method to dismiss virtual keyboard without sending value
   */
  public dismiss(): void {
    this._viewController.dismiss();
  }

  calcCardWidth(){
    let aCount = this.layout.map(row=>{
      return row.reduce( (prev, current, index, array)=>{
        let aCol = current.split(':');
        let width = aCol.length>1?parseInt(aCol[1]):1;
        return prev + width;
      },0);
    });
    let max = Math.max.apply(null, aCount);
    let keyMargin = 8;
    let keyWidth = 64;
    this.minWidth = (keyMargin + keyWidth ) * max; 
  } 

  applyMask(){
    this.value = this.mask.replace(/X/g,'0');
  }

  /**
   * Method to update caret position. This is called on click event in virtual keyboard input element.
   */
  public updateCaretPosition(): void {
    this.virtualKeyboardService.setCaretPosition(this.keyboardInput.nativeElement.selectionStart);
  }

  /**
   * Method to handle actual "key" press from virtual keyboard.
   *  1) Key is "Special", process special key event
   *  2) Key is "Normal"
   *    - Append this key value to input
   *    - Dispatch DOM events to input element
   *    - Toggle Shift key if it's pressed
   *
   * @param {KeyPressInterface} event
   */
  public keyPress(event: KeyPressInterface): void {
    if (event.special) {
      this.handleSpecialKey(event);
    } else {
      this.handleNormalKey(event.keyValue);

      //this.dispatchEvents(event); //@DELETE

      // Toggle shift if it's activated
      if (this.shift) {
        this.virtualKeyboardService.toggleShift();
      }
    }

    this.checkDisabled();
  }

  /**
   * Method to check is virtual keyboard input is disabled.
   */
  private checkDisabled(): void {
    //const maxLength = this.inputElement.nativeElement.maxLength;
    //const valueLength = this.inputElement.nativeElement.value.length;

    //this.disabled = maxLength > 0 && valueLength >= maxLength;
    this.disabled = false;
  }

  /**
   * Method to handle "normal" key press event, this will add specified character to input value.
   *
   * @param {string}  keyValue
   */
  private handleNormalKey(keyValue: string): void {
    let value = '';

   
    let caretPosition = this.caretPosition;
    if(typeof caretPosition == 'undefined') caretPosition = this.mask.length;

    if(this.mask){
      let prefix = this.mask.substr(0,2);
      let val = this.value.substr(3, this.mask.length);
      if(this.value.length >= this.mask.length) value = `${prefix}${val}${keyValue}`
      else value = `${this.value}${keyValue}`;

      if(value.length>this.mask.length) this.value = value.substr(0,this.mask.length-1);
      
      
    }
    else if (!isNaN(this.caretPosition)) {// We have caret position, so attach character to specified position
      value = [
        this.value.slice(0, this.caretPosition),
        keyValue,
        this.value.slice(this.caretPosition)
      ].join('');

      // Update caret position
      this.virtualKeyboardService.setCaretPosition(this.caretPosition + 1);
    } else {
      value = `${this.value}${keyValue}`;
    }

    // And finally set new value to input
    this.value = value;
    this.ionKeyboardInput.setFocus();
  }

  private keyPressHandler(event){
    let keyValue = event.key;
    event.preventDefault();
    if(specialKeys.indexOf(keyValue)!=-1) this.handleSpecialKey({special:true, key:keyValue, keyValue:keyValue})
    else this.handleNormalKey(keyValue);
    
  }

  /**
   * Method to handle "Special" key press events.
   *  1) Enter
   *  2) Escape, close virtual keyboard
   *  3) Backspace, remove last character from input value
   *  4) CapsLock, toggle current layout state
   *  6) Shift, toggle current layout state
   *  5) SpaceBar
   */
  private handleSpecialKey(event: KeyPressInterface): void {
    switch (event.keyValue) {
      case 'Enter':
        this.close();
        break;
      case 'Escape':
        this.close();
        break;
      case 'Backspace':
        const currentValue = this.value;

        // We have a caret position, so we need to remove char from that position
        if (!isNaN(this.caretPosition)) {
          // And current position must > 0
          if (this.caretPosition > 0) {
            const start = currentValue.slice(0, this.caretPosition - 1);
            const end = currentValue.slice(this.caretPosition);

            this.value = `${start}${end}`;

            // Update caret position
            this.virtualKeyboardService.setCaretPosition(this.caretPosition - 1);
          }
        } else {
          this.value = currentValue.substring(0, currentValue.length - 1);
        }

        // Set focus to keyboard input
        this.keyboardInput.nativeElement.focus();
        break;
      case 'CapsLock':
        this.virtualKeyboardService.toggleCapsLock();
        break;
      case 'Shift':
        this.virtualKeyboardService.toggleShift();
        break;
      case 'SpaceBar':
        this.handleNormalKey(' ');
        break;
    }
  }

  /**
   * Method to dispatch necessary keyboard events to current input element.
   *
   * @see https://w3c.github.io/uievents/tools/key-event-viewer.html
   *
   * @param {KeyPressInterface} event
   */
  private dispatchEvents(event: KeyPressInterface) {
    const eventInit: KeyboardEventInit = {
      bubbles: true,
      cancelable: true,
      shiftKey: this.shift,
      key: event.keyValue,
      code: `Key${event.keyValue.toUpperCase()}}`,
      location: 0
    };

    // Simulate all needed events on base element
    this.inputElement.nativeElement.dispatchEvent(new KeyboardEvent('keydown', eventInit));
    this.inputElement.nativeElement.dispatchEvent(new KeyboardEvent('keypress', eventInit));
    this.inputElement.nativeElement.dispatchEvent(new Event('input', {bubbles : true}));
    this.inputElement.nativeElement.dispatchEvent(new KeyboardEvent('keyup', eventInit));

    // And set focus to input
    this.keyboardInput.nativeElement.focus();
  }
}



// WEBPACK FOOTER //
// ./src/modules/utils/ion-virtual-keyboard/ion-virtual-keyboard.component.ts